import { useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceArea, ReferenceDot, ResponsiveContainer, Legend,
} from 'recharts';
import { useRegimen, useRenal, useStrategy } from '@/store/selectors';
import { intervalToHours } from '@/engine';

/** Concentration-time curve over 2–3 intervals (PSD §8.2 / TODO Task 2.11). */
export function ConcentrationCurve(): JSX.Element | null {
  const regimen = useRegimen();
  const renal = useRenal();
  const strategy = useStrategy();

  const data = useMemo(() => {
    if (!regimen) return [];
    const doseMg = regimen.roundedDoseMg ?? regimen.empiricDoseMg;
    const eiInterval = regimen.eiInterval;
    const intervalHr = typeof eiInterval === 'number'
      ? eiInterval
      : intervalToHours(eiInterval ?? null) || regimen.empiricIntervalHr;
    if (!intervalHr || intervalHr <= 0) return [];

    const infusionHr = strategy.infusionDurationMin / 60;
    const totalHr = intervalHr * 3;
    // Use patient-specific Kel from renal engine; fall back to a conservative 0.08 if unavailable.
    const kel = renal?.populationKelPerHr ?? 0.08;
    const points: Array<{ time: number; conc: number }> = [];

    for (let t = 0; t <= totalHr; t += 0.25) {
      const cycleT = t % intervalHr;
      let conc: number;
      if (cycleT < infusionHr) {
        conc = doseMg * (1 - Math.exp(-kel * cycleT)) / (infusionHr * kel);
      } else {
        const peakAtInfusionEnd = doseMg * (1 - Math.exp(-kel * infusionHr)) / (infusionHr * kel);
        conc = peakAtInfusionEnd * Math.exp(-kel * (cycleT - infusionHr));
      }
      points.push({ time: Number(t.toFixed(2)), conc: Number(conc.toFixed(2)) });
    }
    return points;
  }, [regimen, renal?.populationKelPerHr, strategy.infusionDurationMin]);

  if (!regimen || data.length === 0) {
    return (
      <section className="panel panel-result" aria-label="Concentration-time curve" data-testid="concentration-curve">
        <h2 className="panel-title">Concentration-Time Profile</h2>
        <div className="empty-state">Enter patient data and compute a regimen to see the concentration-time curve.</div>
      </section>
    );
  }

  const peakTarget = regimen.predictedPeakMg ?? 0;
  const troughTarget = regimen.predictedTroughMg ?? 0;

  return (
    <section className="panel panel-result" aria-label="Concentration-time curve" data-testid="concentration-curve">
      <h2 className="panel-title">Concentration-Time Profile</h2>
      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--amg-border-default)" />
          <XAxis
            dataKey="time"
            type="number"
            domain={[0, 'dataMax']}
            label={{ value: 'Time (hr)', position: 'insideBottom', offset: -2 }}
            tick={{ fontSize: 10 }}
          />
          <YAxis
            label={{ value: 'Conc (mg/L)', angle: -90, position: 'insideLeft' }}
            tick={{ fontSize: 10 }}
          />
          <Tooltip formatter={(v: number) => `${v.toFixed(1)} mg/L`} labelFormatter={(l) => `${l} hr`} />
          <Legend />
          {peakTarget > 0 && (
            <ReferenceArea y1={peakTarget * 0.8} y2={peakTarget * 1.2} fill="var(--amg-status-safe-bg)" />
          )}
          {troughTarget > 0 && (
            <ReferenceArea y1={0} y2={troughTarget} fill="var(--amg-status-warn-bg)" />
          )}
          <Line
            type="monotone"
            dataKey="conc"
            stroke="var(--amg-brand-primary)"
            strokeWidth={2}
            dot={false}
            name="Predicted concentration"
          />
          {regimen.predictedPeakMg != null && (
            <ReferenceDot x={strategy.infusionDurationMin / 60} y={regimen.predictedPeakMg} r={4} fill="var(--amg-brand-primary)" stroke="white" />
          )}
        </LineChart>
      </ResponsiveContainer>
      <p className="hint-text">Predicted serum concentration over 3 dosing intervals using a one-compartment intermittent infusion model. Green band = peak target ±20%; amber band = trough threshold.</p>
    </section>
  );
}
